import { Box, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { sendDelete, sendGet, sendPostFormData, sendPut } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { ErrorDisplay, useErrorDispatch } from 'ContextProviders/ErrorProvider'
import { type DocumentTypeRequirement, DocumentTarget, type DocumentData, type DocumentCaregiver } from 'core/types'
import { DocumentListEdit } from 'Components/DocumentList/DocumentListEdit'
import { BannerModal } from 'Components/Modal/BannerModal'
import { DownloadDocument } from 'core/Utilities'

export interface DocumentListProps {
  childID: number | null
  caregiverID: number | null
  editable: boolean
  canVerify: boolean
  showDisclaimer: boolean
  isdId: number
  documentRequirements?: DocumentTypeRequirement[]
  dataChanged?: () => void
  years: number[]
}

export interface documentTypeModel {
  documentTypeID: number
  name: string
  documentDescription: string | null
  imageFile: string | null
  required: boolean
  documents: DocumentData[]
  documentStatus: string
  documentTarget?: DocumentTarget
  incomeRelated: boolean
}

export function DocumentList (props: DocumentListProps): JSX.Element {
  const [loaded, setLoaded] = useState(false)
  const [documentList, setDocumentList] = useState<documentTypeModel[]>([])
  const [refreshTime, setRefreshTime] = useState(new Date())
  const errorDispatch = useErrorDispatch()
  const [hasNoAccess, setHasNoAccess] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState({ id: 0, show: false, verified: false })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (props.isdId === 0) {
        setLoaded(true)
        return
      }

      let typeList: string = ''
      let docs: DocumentTypeRequirement[] = []
      if (props.documentRequirements === undefined) {
        const { response: rsp, success: suc } = await sendGet(`/Document/DocumentTypes?isdId=${props.isdId}`, {})
        if (suc) {
          docs = rsp as DocumentTypeRequirement[]
        }
      } else {
        docs = props.documentRequirements ?? []
      }

      docs.forEach(i => {
        if (typeList !== '') {
          typeList += ';'
        }
        if (props.caregiverID != null) {
          if (i.documentTarget === DocumentTarget.ADULT || i.documentTarget === DocumentTarget.BOTH) {
            typeList += `${i.documentTypeID},${(i.required ? '1' : '0')}`
          }
        } else if (props.childID != null) {
          if (i.documentTarget === DocumentTarget.CHILD || i.documentTarget === DocumentTarget.BOTH) {
            typeList += `${i.documentTypeID},${(i.required ? '1' : '0')}`
          }
        }
      })

      let queryString: string = ''
      if (props.caregiverID != null) {
        queryString = `?caregiverID=${props.caregiverID.toString()}`
      } else if (props.childID != null) {
        queryString = `?childID=${props.childID.toString()}`
      }
      // construct the documentTypes parameters which is a set of value pairs separated where the first is a documentTypeID and the second is either 0 or 1 indicating if it's requried.
      // each value set is separated by ; and the 2 values are separated by ,

      const { response, success } = await sendGet(`/Document/DocumentList${queryString}&documentTypes=${typeList}`, {})
      if (success && (Boolean(response.success))) {
        setDocumentList(response.result.documentTypes)
        errorDispatch({ errors: [], type: 'clear' })
      } else {
        if (response.status === 401) {
          setHasNoAccess(true)
        } else {
          errorDispatch({ errors: response.errors, type: 'update' })
        }
      }
      setLoaded(true)
    }
    void fetchData()
  }, [refreshTime, props.isdId])

  const uploadFile = async (documentTypeID: number, file: File, year: number | null): Promise<boolean> => {
    const fileUrl = '/Document/UploadFile'
    const fileData = new FormData()
    fileData.append('documentTypeID', documentTypeID.toString())
    if (props.caregiverID !== null) {
      fileData.append('caregiverID', props.caregiverID.toString())
    } else if (props.childID !== null) {
      fileData.append('childID', props.childID.toString())
    }
    if (year !== null) {
      fileData.append('incomeYear', year.toString())
    }
    fileData.append('file', file)

    const { error, success } = await sendPostFormData(fileUrl, fileData)
    if (success) {
      setRefreshTime(new Date())
      toast.success('Document has been uploaded')
      if (props.dataChanged !== undefined) {
        props.dataChanged()
      }
      return true
    } else {
      toast.error(error)
    }
    return false
  }

  const onVerifiying = async (documentTypeId: number, index: number, verified: boolean, verifiedNotes: string, assigedIds: DocumentCaregiver[],
    incomeAmount: number | null, incomeYear: number | null
  ): Promise<boolean> => {
    if (documentList !== undefined) {
      const doc = documentList?.find(d => d.documentTypeID === documentTypeId)?.documents[index] ?? null
      if (doc !== undefined && doc !== null) {
        if (assigedIds.length > 0 || !verified) {
          const modifiedDoc: DocumentData = { ...doc, verified, caregivers: assigedIds, verificationNote: verifiedNotes, incomeAmount, incomeYear }
          const { success, error } = await sendPut('/Document/UpdateDocumentVerifyDetails', Object(modifiedDoc))
          if (success) {
            const successMessage = verified ? 'Document Verified' : 'Document Unverified'
            toast.success(successMessage)
            setRefreshTime(new Date())
          } else {
            error.forEach(err => {
              toast.error(err)
            })
          }
        } else if (verified && assigedIds.length === 0) {
          toast.error('Please select at least one caregiver')
        } else {
          toast.error('Verification failed.')
        }
      }
    }
    return true
  }

  const downloadDocument = async (documentId: number): Promise<boolean> => {
    return DownloadDocument(documentId)
  }

  const onDeleteDocument = async (documentId: number, documentTypeID: number, index: number, verified: boolean): Promise<boolean> => {
    if (documentId !== undefined) {
      setShowDeleteModal({ id: documentId, show: true, verified })
      return true
    }
    return false
  }

  const onConfirmDelete = async (): Promise<boolean> => {
    const url = `/Document/Delete/${showDeleteModal.id.toString()}`
    const { error, success } = await sendDelete(url)
    if (success) {
      setRefreshTime(new Date())
      toast.success('Document has been deleted')
      if (props.dataChanged !== undefined) {
        props.dataChanged()
      }
      setShowDeleteModal({ id: 0, show: false, verified: false })
      return true
    } else {
      toast.error(error)
      return false
    }
  }

  if (hasNoAccess) {
    return <div>You do not have access to documents</div>
  } else if (!loaded) {
    return <CircularProgress role='loading' />
  }

  return <>
    <ErrorDisplay fieldName='' />
    <Box width={'100%'}>
      <DocumentListEdit
        documentTypes={documentList ?? []}
        editable={props.editable}
        showDisclaimer={props.showDisclaimer}
        canVerify={props.canVerify}
        onDelete={onDeleteDocument}
        onDownload={downloadDocument}
        onVerify={onVerifiying}
        onUpload={uploadFile}
        incomeRecords={null}
      />
    </Box>
    <BannerModal
      title='Delete Document'
      content={<Box>Are you sure you want to delete this {showDeleteModal.verified ? 'verified ' : ''}document?</Box>}
      onConfirm={onConfirmDelete}
      open={showDeleteModal.show}
      onClose={() => { setShowDeleteModal({ id: 0, show: false, verified: false }) }}
    />
  </>
}
